"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _propTypes2 = _interopRequireDefault(require("@styled-system/prop-types"));

var _tags = _interopRequireDefault(require("../../utils/helpers/tags"));

var _icon = _interopRequireDefault(require("./icon.style"));

var _tooltip = _interopRequireDefault(require("../tooltip"));

var _utils = require("../../style/utils");

var _logger = _interopRequireDefault(require("../../utils/logger"));

var _iconConfig = require("./icon-config");

var _tooltipProvider = require("../../__internal__/tooltip-provider");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function () { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

let deprecatedWarnTriggered = false;
const marginPropTypes = (0, _utils.filterStyledSystemMarginProps)(_propTypes2.default.space);

const Icon = /*#__PURE__*/_react.default.forwardRef(({
  bg,
  bgShape,
  bgSize,
  bgTheme,
  className,
  color,
  disabled,
  fontSize,
  iconColor,
  type,
  tooltipMessage,
  tooltipPosition,
  tooltipVisible,
  tooltipBgColor,
  tooltipFontColor,
  tooltipFlipOverrides,
  tabIndex,
  isPartOfInput,
  inputSize,
  ...rest
}, ref) => {
  if (!deprecatedWarnTriggered && (iconColor || bgTheme)) {
    deprecatedWarnTriggered = true;

    _logger.default.deprecate("`iconColor` and `bgTheme` props are deprecated and will soon be removed");
  }

  const isInteractive = !!tooltipMessage && !disabled;
  /** Return Icon type with overrides */

  const iconType = () => {
    // switch tweaks icon names for actual icons in the set
    switch (type) {
      case "help":
        return "question";

      case "maintenance":
        return "settings";

      case "new":
        return "gift";

      case "success":
        return "tick";

      case "messages":
      case "email":
        return "message";

      default:
        return type;
    }
  };

  const styleProps = {
    bg,
    bgTheme,
    bgSize,
    bgShape,
    color,
    disabled,
    fontSize,
    isInteractive,
    iconColor,
    tabIndex,
    type: iconType(),
    ...(0, _utils.filterStyledSystemMarginProps)(rest)
  };

  const icon = /*#__PURE__*/_react.default.createElement(_icon.default, _extends({
    ref: ref,
    key: "icon",
    className: className || null,
    "data-element": iconType()
  }, (0, _tags.default)("icon", rest), styleProps));

  if (tooltipMessage) {
    const visible = disabled ? false : tooltipVisible;
    const {
      tooltipPosition: tooltipPositionFromContext
    } = (0, _react.useContext)(_tooltipProvider.TooltipContext);
    return /*#__PURE__*/_react.default.createElement(_tooltip.default, {
      message: tooltipMessage,
      position: tooltipPositionFromContext || tooltipPosition,
      type: type,
      isVisible: visible,
      isPartOfInput: isPartOfInput,
      inputSize: inputSize,
      bgColor: tooltipBgColor,
      fontColor: tooltipFontColor,
      flipOverrides: tooltipFlipOverrides
    }, icon);
  }

  return icon;
});

Icon.propTypes = { ...marginPropTypes,

  /**
   * @private
   * @ignore
   * Add classes to this component
   * */
  className: _propTypes.default.string,

  /** Icon type */
  type: _propTypes.default.string.isRequired,

  /** Background size */
  bgSize: _propTypes.default.oneOf(["extra-small", "small", "medium", "large", "extra-large"]),

  /** Background shape */
  bgShape: _propTypes.default.oneOf(["circle", "rounded-rect", "square"]),

  /** Background color theme */
  bgTheme: _propTypes.default.oneOf(["info", "error", "success", "warning", "business", "none"]),

  /** Icon font size */
  fontSize: _propTypes.default.oneOf(["small", "medium", "large", "extra-large"]),

  /** Icon color */
  iconColor: _propTypes.default.oneOf(["default", "on-light-background", "on-dark-background", "business-color"]),

  /** Override iconColor, provide any color from palette or any valid css color value. */
  color: _propTypes.default.string,

  /** Override bgTheme, provide any color from palette or any valid css color value. */
  bg: _propTypes.default.string,

  /** Sets the icon in the disabled state */
  disabled: _propTypes.default.bool,

  /** Aria label for accessibility purposes */
  ariaLabel: _propTypes.default.string,

  /** The message to be displayed within the tooltip */
  tooltipMessage: _propTypes.default.node,

  /** The position to display the tooltip */
  tooltipPosition: _propTypes.default.oneOf(["top", "bottom", "left", "right"]),

  /** Control whether the tooltip is visible */
  tooltipVisible: _propTypes.default.bool,

  /** Override background color of the Tooltip, provide any color from palette or any valid css color value. */
  tooltipBgColor: _propTypes.default.string,

  /** Override font color of the Tooltip, provide any color from palette or any valid css color value. */
  tooltipFontColor: _propTypes.default.string,

  /** Overrides the default flip behaviour of the Tooltip, must be an array containing some or all of ["top", "bottom", "left", "right"] (see https://popper.js.org/docs/v2/modifiers/flip/#fallbackplacements) */
  tooltipFlipOverrides: (props, propName) => {
    const prop = props[propName];
    const isValid = prop && Array.isArray(prop) && prop.every(placement => _iconConfig.ICON_TOOLTIP_POSITIONS.includes(placement));

    if (!prop || isValid) {
      return null;
    }

    return new Error( // eslint-disable-next-line max-len
    `The \`${propName}\` prop supplied to \`Icon\` must be an array containing some or all of ["top", "bottom", "left", "right"].`);
  },

  /** @ignore @private */
  isPartOfInput: _propTypes.default.bool,

  /** @ignore @private */
  inputSize: _propTypes.default.oneOf(["small", "medium", "large"]),

  /** @ignore @private */
  tabIndex: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.number])
};
Icon.defaultProps = {
  bgSize: "small",
  fontSize: "small",
  disabled: false
};
var _default = Icon;
exports.default = _default;